import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useUser } from '../contexts/UserContext';
import { useNotification } from '../contexts/NotificationContext';
import { useLanguage, useTranslation } from '../i18n';
import { InsufficientBalanceModal } from './InsufficientBalanceModal';
import { PageHeader } from './PageHeader';
import type { Meditation } from '../types';
import './MeditationDetailPage.css';

export const MeditationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, updateBalance } = useUser();
  const { showSuccess } = useNotification();
  const { language } = useLanguage();
  const t = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  const [meditation, setMeditation] = useState<Meditation | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasPaid, setHasPaid] = useState(false);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  const [insufficientBalanceData, setInsufficientBalanceData] = useState({ required: 0, available: 0 });
  const [currentBalance, setCurrentBalance] = useState<number>((user as any)?.balance || 0);

  // Check if meditation has audio
  const hasAudio = meditation?.audio_url;
  const hasCover = meditation?.cover_url;

  // Reload meditation when id or language changes
  useEffect(() => {
    if (id) {
      loadMeditation();
    }
  }, [id, language]);

  useEffect(() => {
    if (user) {
      setCurrentBalance((user as any)?.balance || 0);
    }
  }, [user]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video || hasAudio) return; // Skip if using audio

    const handleEnded = async () => {
      if (meditation && (hasPaid || meditation.cost === 0)) {
        await handleComplete();
      }
    };

    const handlePlay = async (e: Event) => {
      if (meditation && !hasPaid && !meditation.is_paid && meditation.cost > 0) {
        e.preventDefault();
        e.stopPropagation();
        video.pause();
        video.currentTime = 0;
        
        alert(t.meditation.costAlert.replace('{cost}', String(meditation.cost)));
        return false;
      }

      if (meditation && !hasPaid && meditation.cost === 0) {
        try {
          const response = await apiClient.startMeditation(meditation.id);
          if (response.success) {
            setHasPaid(true);
            setMeditation({ ...meditation, is_paid: true, paid_at: response.data.paid_at, is_started: true });
          }
        } catch (error) {
          console.error('Failed to track free meditation start:', error);
        }
      }

      if (video.muted) {
        video.muted = false;
      }
    };

    const handlePlaying = (e: Event) => {
      if (meditation && !hasPaid && !meditation.is_paid && meditation.cost > 0) {
        e.preventDefault();
        e.stopPropagation();
        video.pause();
        video.currentTime = 0;
        return false;
      }
    };

    video.addEventListener('ended', handleEnded);
    video.addEventListener('play', handlePlay);
    video.addEventListener('playing', handlePlaying);
    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [meditation, hasPaid, t, hasAudio]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !hasAudio) return;

    const handleEnded = async () => {
      if (meditation && (hasPaid || meditation.cost === 0)) {
        await handleComplete();
      }
    };

    const handlePlay = async (e: Event) => {
      if (meditation && !hasPaid && !meditation.is_paid && meditation.cost > 0) {
        e.preventDefault();
        e.stopPropagation();
        audio.pause();
        audio.currentTime = 0;
        
        alert(t.meditation.costAlert.replace('{cost}', String(meditation.cost)));
        return false;
      }

      if (meditation && !hasPaid && meditation.cost === 0) {
        try {
          const response = await apiClient.startMeditation(meditation.id);
          if (response.success) {
            setHasPaid(true);
            setMeditation({ ...meditation, is_paid: true, paid_at: response.data.paid_at, is_started: true });
          }
        } catch (error) {
          console.error('Failed to track free meditation start:', error);
        }
      }
    };

    const handlePlaying = (e: Event) => {
      if (meditation && !hasPaid && !meditation.is_paid && meditation.cost > 0) {
        e.preventDefault();
        e.stopPropagation();
        audio.pause();
        audio.currentTime = 0;
        return false;
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('playing', handlePlaying);
    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('playing', handlePlaying);
    };
  }, [meditation, hasPaid, t, hasAudio]);

  const loadMeditation = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getMeditation(id!);
      setMeditation(response.data);
      setHasPaid(response.data.is_paid);
      
      console.log('Meditation loaded:', {
        title: response.data.title,
        cost: response.data.cost,
        is_paid: response.data.is_paid,
        is_free: response.data.is_free,
        audio_url: response.data.audio_url,
        cover_url: response.data.cover_url
      });
    } catch (error) {
      console.error('Failed to load meditation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!meditation) return;

    if (meditation.is_paid || hasPaid) {
      playMedia();
      return;
    }

    try {
      const response = await apiClient.startMeditation(meditation.id);
      
      if (response.success) {
        setHasPaid(true);
        setMeditation({ ...meditation, is_paid: true, paid_at: response.data.paid_at });
        
        if (response.data?.balance !== undefined) {
          setCurrentBalance(response.data.balance);
        }
        
        playMedia();
      }
    } catch (error: any) {
      console.error('Failed to start meditation:', error);
      
      if (error.response?.status === 402) {
        const errorData = error.response.data;
        setInsufficientBalanceData({
          required: errorData.required || meditation.cost,
          available: errorData.available || (user as any)?.balance || 0
        });
        setShowInsufficientBalanceModal(true);
      } else {
        alert(t.meditation.notFound);
      }
    }
  };

  const playMedia = () => {
    if (hasAudio && audioRef.current) {
      audioRef.current.play();
    } else if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.play();
    }
  };

  const handleComplete = async () => {
    if (!meditation || meditation.is_completed) return;

    try {
      const userLocalHour = new Date().getHours();
      
      const response = await apiClient.completeMeditation(meditation.id, userLocalHour);
      setMeditation({ ...meditation, is_completed: true });
      
      if (response.data?.completed_tasks && response.data.completed_tasks.length > 0) {
        response.data.completed_tasks.forEach((task: any) => {
          showSuccess(
            `✨ ${task.title} +${task.reward} 💎`,
            5000
          );
        });
        
        if (response.data?.balance !== undefined) {
          updateBalance(response.data.balance);
          setCurrentBalance(response.data.balance);
        }
      }
    } catch (error) {
      console.error('Failed to complete meditation:', error);
    }
  };

  const getDifficultyLabel = (difficulty: string): string => {
    // Use translated label from API if available
    if (meditation?.difficulty_label) {
      return meditation.difficulty_label;
    }
    // Fallback to frontend translations
    const difficultyMap: { [key: string]: string } = {
      beginner: t.meditation.beginner,
      intermediate: t.meditation.intermediate,
      advanced: t.meditation.advanced
    };
    return difficultyMap[difficulty] || difficulty;
  };

  const getCategoryLabel = (category: string): string => {
    // Use translated label from API if available
    if (meditation?.category_label) {
      return meditation.category_label;
    }
    // Fallback to frontend translations
    const categoryMap: { [key: string]: string } = {
      breathing: t.meditation.breathing,
      meditation: t.meditation.meditationType,
      relaxation: t.meditation.relaxation,
      anxiety: t.meditation.anxiety,
      sleep: t.meditation.sleep
    };
    return categoryMap[category] || category;
  };

  const getLocale = () => {
    switch (language) {
      
      
      default: return 'es-ES';
    }
  };

  if (loading) {
    return (
      <div className="page-container meditation-detail-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t.meditation.loadingMeditation}</p>
        </div>
      </div>
    );
  }

  if (!meditation) {
    return (
      <div className="page-container meditation-detail-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t.meditation.notFound}</p>
          <button onClick={() => navigate('/meditations')} className="btn btn-primary">
            {t.meditation.backToCatalog}
          </button>
        </div>
      </div>
    );
  }

  const isLocked = !hasPaid && !meditation.is_paid && meditation.cost > 0;
  const canPlay = hasPaid || meditation.is_paid || meditation.cost === 0;

  return (
    <div className="page-container meditation-detail-page">
      <PageHeader title={t.nav.meditations} onBack={() => navigate('/meditations')} />

      <div className="media-section">
        {/* Show cover image with audio player OR video player */}
        {hasAudio ? (
          // Audio meditation with cover
          <div className="audio-meditation-container">
            {hasCover && (
              <div className="cover-container">
                <img 
                  src={meditation.cover_url} 
                  alt={meditation.title}
                  className="meditation-cover-image"
                />
                {isLocked && (
                  <div className="locked-overlay">
                    <div className="locked-content">
                      <div className="locked-icon">🔒</div>
                      <div className="locked-text">
                        {t.meditation.requiresCrystals.replace('{cost}', String(meditation.cost))}
                      </div>
                    </div>
                  </div>
                )}
                {meditation.is_completed && (
                  <div className="completed-overlay">
                    <div className="completed-badge">✓ {t.meditation.completed}</div>
                  </div>
                )}
              </div>
            )}
            
            <div className="audio-player-container">
              <div className="audio-player-label">🎧 {t.meditation.audioMeditation || 'Аудио медитация'}</div>
              <audio
                ref={audioRef}
                className="meditation-audio"
                controls={canPlay}
                preload="metadata"
                style={{ pointerEvents: canPlay ? 'auto' : 'none' }}
              >
                <source src={meditation.audio_url} />
              </audio>
            </div>
          </div>
        ) : (
          // Video meditation
          <div className="video-container">
            <video
              ref={videoRef}
              className="meditation-video"
              controls={canPlay}
              preload="metadata"
              playsInline
              crossOrigin="anonymous"
              muted
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture={isLocked}
              disableRemotePlayback={isLocked}
              style={{ pointerEvents: canPlay ? 'auto' : 'none' }}
            >
              <source src={meditation.video_url} type="video/mp4" />
            </video>
            
            {isLocked && (
              <div className="locked-overlay">
                <div className="locked-content">
                  <div className="locked-icon">🔒</div>
                  <div className="locked-text">
                    {t.meditation.requiresCrystals.replace('{cost}', String(meditation.cost))}
                  </div>
                </div>
              </div>
            )}
            
            {meditation.is_completed && (
              <div className="completed-overlay">
                <div className="completed-badge">✓ {t.meditation.completed}</div>
              </div>
            )}
          </div>
        )}

        {isLocked && (
          <div className="start-section">
            <button className="btn btn-start" onClick={handleStart}>
              <span>{t.meditation.startFor}</span>
              <span className="cost">💎 {meditation.cost}</span>
            </button>
            <p className="start-hint">
              {t.meditation.yourBalance} 💎 {currentBalance}
            </p>
          </div>
        )}
      </div>

      <div className="meditation-content">
        <div className="meditation-header">
          <h1 className="meditation-title">{meditation.title}</h1>
          <div className="meditation-meta">
            <span className="meta-badge">{getDifficultyLabel(meditation.difficulty)}</span>
            <span className="meta-badge">{getCategoryLabel(meditation.category)}</span>
            <span className="meta-badge">{meditation.duration} {t.common.minutes}</span>
            {meditation.cost > 0 && (
              <span className="meta-badge cost-badge">💎 {meditation.cost}</span>
            )}
            {hasAudio && (
              <span className="meta-badge audio-badge">🎧</span>
            )}
          </div>
          
          {meditation.is_paid && meditation.paid_at && (
            <div className="payment-info">
              {meditation.cost > 0 ? (
                <>
                  <span className="payment-icon">✓</span>
                  <span className="payment-text">
                    {t.meditation.paid} {new Date(meditation.paid_at).toLocaleDateString(getLocale(), {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </>
              ) : (
                <>
                  <span className="payment-icon">🎁</span>
                  <span className="payment-text">{t.meditation.free}</span>
                </>
              )}
            </div>
          )}
        </div>

        {meditation.author && (
          <div className="author-block">
            <div className="author-info">
              {meditation.author.photo_url ? (
                <img src={meditation.author.photo_url} alt={meditation.author.name} className="author-photo" />
              ) : (
                <div className="author-photo-placeholder">
                  {meditation.author.name.charAt(0)}
                </div>
              )}
              <div className="author-details">
                <div className="author-label">{t.meditation.author}</div>
                <div className="author-name">{meditation.author.name}</div>
                {meditation.author.bio && (
                  <div className="author-bio">{meditation.author.bio}</div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="info-block">
          <h2 className="block-title">{t.meditation.description}</h2>
          <p className="block-content">{meditation.description}</p>
        </div>

        <div className="info-block">
          <h2 className="block-title">{t.meditation.instructions}</h2>
          <div className="block-content instructions">
            {meditation.instructions.split('\n').map((line, index) => (
              <p key={index}>{line}</p>
            ))}
          </div>
        </div>

        {meditation.is_completed && (
          <div className="completion-message">
            <div className="completion-icon">🎉</div>
            <h3>{t.meditation.meditationCompleted}</h3>
            <p>{t.meditation.greatJob}</p>
          </div>
        )}
      </div>

      <InsufficientBalanceModal
        isOpen={showInsufficientBalanceModal}
        onClose={() => setShowInsufficientBalanceModal(false)}
        required={insufficientBalanceData.required}
        available={insufficientBalanceData.available}
      />
    </div>
  );
};
